import { head, last, take, takeLast, isNil, curry } from 'ramda'
import { isNumber, isArray } from 'utils/types'

/**
 * Returns a list of numbers from `from` (inclusive) to `to` (inclusive)
 *
 * @param {Number} from The first number in the list.
 * @param {Number} to The last number in the list.
 * @param {Number} step The number to increment.
 * @return {Array} The list of numbers in the set.
 * @example
 *
 *     inclusiveRange(0, 3) //=> [0, 1, 2, 3]
 *     inclusiveRange(0, 2, .5) //=> [0, .5, 1, 1.5, 2]
 */
export const inclusiveRange = (from, to, step = 1) => {
  if (!(isNumber(from) && isNumber(to) && isNumber(step))) {
    throw new TypeError('All argumnets to inclusiveRange must be numbers')
  }

  const result = []
  let n = from

  while (n <= to) {
    result.push(n)
    n += step
  }

  return result
}

/**
 * Returns a number value that is the center of the line. Does not respect the origin of given parentContainer
 *
 * @param {Number} parentContainer the container in which you are wanting to fit the child into.
 * @param {Number} childContainer the child container which may be bigger than the parent container.
 * @param {Number} sideOffset1 offset bound 1.
 * @param {Number} sideOffset2 offset bound 2.
 * @return {Number} The total offset to react center.
 * @example
 *
 *     getOffsetToReachCenterOfAxis(100, 200, 0, 0) //=> -50
 *     getOffsetToReachCenterOfAxis(100, 50, 10, 20) //=> 10
 *
 *     getOffsetToReachCenterOfAxis(10, 6, 1, 2) //=> .5,
 *      10: parent container -> center is 5 \
 *                                           center is 2
 *      6: child container  -> center is 3  /
 *
 *      1: sideOffset1      -> center is 0.5 \
 *                                            total offset from center is 1.5
 *      2: sideOffset2      -> center is 1   /
 *
 *      Line needs to be 0.5 to reach center
 */
export const getOffsetToReachCenterOfAxis = (
  parentContainer = 0,
  childContainer = 0,
  sideOffset1 = 0,
  sideOffset2 = 0
) =>
  parentContainer / 2 - childContainer / 2 - sideOffset1 / 2 + sideOffset2 / 2

/**
 * Returns the two numbers in a list that the value supplied falls between
 * NOTE: this requires the list to be sorted previously
 *
 * @param {Number} value
 * @param {Array} list
 * @return {Array} the upper/lower bound
 * @example
 *
 *    closestValues(9, [1, 4, 30, 30])
 *    // => [4, 30]
 *
 *    closestValues(4, [6, 7, 9])
 *    // => [6, 7]
 *
 *    closestValues(10, [1, 3, 4])
 *    // => [3, 4]
 *
 */
export const closestValues = (value, sortedList) => {
  if (!isArray(sortedList)) throw new TypeError('`sortedList` must be an array')

  if (sortedList.length <= 2) return sortedList

  const minValue = head(sortedList)
  const maxValue = last(sortedList)

  if (value < minValue) return take(2, sortedList)
  if (value > maxValue) return takeLast(2, sortedList)

  return sortedList.reduce(
    (acc, current) => {
      // also accts as prev
      const lowerBound = head(acc)
      const upperBound = last(acc)

      // we have already found our values
      if (upperBound) return acc

      if (value > lowerBound && value < current) {
        return [lowerBound, current]
      }

      return [current, null]
    },
    [0, null]
  )
}

/**
 * Returns half of the value supplied
 *
 * @param {Number} value
 * @return {Number}
 * @example
 *
 *    half(4) // => 2
 */
export const half = (value) => {
  if (!isNumber(value)) throw new TypeError('argument must be a number')

  return value / 2
}

/**
 * Returns the min of two values entered
 * If one of the values is null, then the other is returned
 *
 * @param {Number} a
 * @param {Number} b
 * @return {Number}
 * @example
 *
 *    safeMin(4, 2) // => 2
 *    safeMin(4, null) //=> 4
 */
export const safeMin = curry((a, b) => {
  if (isNil(a)) return b
  if (isNil(b)) return a

  return a < b ? a : b
})

/**
 * Returns the max of two values entered
 * If one of the values is null, then the other is returned
 *
 * @param {Number} a
 * @param {Number} b
 * @return {Number}
 * @example
 *
 *    safeMax(4, 2) // => 4
 *    safeMin(4, null) //=> 4
 */
export const safeMax = curry((a, b) => {
  if (isNil(a)) return b
  if (isNil(b)) return a

  return a > b ? a : b
})

/**
 * Check if a number is a negative number
 * @param {Number} num
 */
export const isNegative = (num) => Math.sign(num) === -1
